import { useNavigate, useSearchParams } from 'react-router-dom';
import { usePostInitOTP, usePostOTPLogin } from '@/services/hooks';
import { useEffect, useState } from 'react';
import OTP from 'src/components/otp';
import ozone_login_logo from '@/assets/logos/ozone-colorfull-logo.svg';
import Container from '@/components/@base/container';
import useHomeStore from '@/store/home';
import { addToTime } from '@/lib/date';
import PaymentTimeExpiration from '@/components/payment-time-expiration';
import { useTimer } from 'react-timer-hook';
import useCommonModalStore from '@/store/common-modal-store';
import useOTPAuthStore from '@/store/otp-auth-store';
import { AxiosError } from 'axios';
import WrongPgc from '@/components/wrong-pgc';
import FontIcon from '@/components/@base/font-icon';
import cn from '@/lib/clsxm';
import { changeFavicon, isMerchantUpg } from '@/lib/helper';
import localization from '@/constant/localization';

const Login = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const isAuthenticated = !!localStorage.getItem('access_token');
  const pgc = searchParams.get('pgc');

  useEffect(() => {
    if (!!localStorage.getItem('pgc') && localStorage.getItem('pgc') !== pgc) {
      localStorage.clear();
    }
    if (pgc) localStorage.setItem('pgc', pgc);
    if (pgc && isAuthenticated) navigate(`/?pgc=${searchParams.get('pgc')}`);
  }, []);
  const { mutate: reqPostOTP, isPending: loginPending } = usePostOTPLogin();
  const { setOtpTokenLength, setTimerExpired } = useHomeStore();
  const { paymentTime, setPaymentTime } = useOTPAuthStore();
  const { setShow } = useCommonModalStore();
  const { mutate: reqInitOTP, data: initData, isPending } = usePostInitOTP();
  const [manualLoading, setManualLoading] = useState<boolean>(true);
  const [isWrongPgc, setWrongPgc] = useState(false);
  const { minutes, seconds, restart } = useTimer({
    expiryTimestamp: paymentTime || new Date(new Date().getTime() + 50000),
    onExpire: () => {
      setTimerExpired(true);
      initData && showTimeExpireModal();
    },
  });
  useEffect(() => {
    reqInitOTP(
      {
        paymentGatewayCode: pgc!,
      },
      {
        onSuccess: (res) => {
          localStorage.setItem('expiredAt', res?.invoiceExpireTime?.toString());
          if (res.invoiceStatusId === 10 && res.ipgurl) {
            location.replace(res.ipgurl);
          } else if (res.accessToken) {
            localStorage.setItem('access_token', res.accessToken);
            localStorage.setItem('merchantRedirectURL', res.merchantRedirectURL);
            location.replace(location.origin + `/?pgc=${pgc}`);
          } else if (res.invoiceStatusId === 40) {
            location.replace(res.merchantRedirectURL);
            setManualLoading(false);
          } else {
            localStorage.setItem('merchantRedirectURL', res.merchantRedirectURL);
            setOtpTokenLength(res.tokenLength);
            setManualLoading(false);
          }
        },
        onError: (err) => {
          const returnError = err as AxiosError;
          if (returnError?.response?.status === 400) setWrongPgc(true);
          setManualLoading(false);
        },
      },
    );
  }, []);

  const showTimeExpireModal = () => {
    localStorage.getItem('expiredAt') &&
      setShow(true, {
        onDemandClose: false,
        grayScaleBackDrop: true,
        dialogPanelClassName: 'p-0 w-[280px]',
        center: true,
        Body: () => <PaymentTimeExpiration />,
      });
  };
  useEffect(() => {
    if (initData?.clientMobileNumber) {
      sessionStorage.setItem('phoneNumber', initData.clientMobileNumber);
    }
    if (initData?.invoiceExpireTime) {
      const time = addToTime(new Date(), initData.invoiceExpireTime, { unit: 'SECONDS' });
      restart(time);
      setPaymentTime(time);
    }
    if (initData?.isEpg && !isPending) {
      sessionStorage.setItem(
        'mUpg',
        JSON.stringify({
          logo: initData?.epgLogo,
          title: initData?.epgTitle,
        }),
      );
      document.title = `${initData?.epgTitle}`;
    } else if(!initData?.isEpg && !isPending){
      setTimeout(()=> {
        document.title = localization.ozoneGateWay;
      }, 2000)
      changeFavicon('/favicon.ico');
    }
  }, [initData]);

  const handleSubmit = (otp: string) => {
    initData?.clientMobileNumber &&
      reqPostOTP(
        {
          mobile: initData?.clientMobileNumber,
          token: otp,
          paymentGatewayCode: pgc!,
        },
        {
          onSuccess: (res) => {
            localStorage.setItem('access_token', res.accessToken);
            location.replace(location.origin + `/?pgc=${pgc}`);
          },
        },
      );
  };

  const redirectUrl = () => {
    initData?.merchantRedirectURL && location.replace(initData?.merchantRedirectURL);
  };
  if (isWrongPgc) return <WrongPgc />;
  return (
    <Container className='relative mx-auto flex h-full min-h-screen w-full max-w-[448px] flex-col items-center px-4 dark:bg-grayDark-950'>
      <FontIcon
        icon='icon-arrow-right'
        className='absolute right-3.5 top-4 cursor-pointer text-2xl text-black dark:text-white'
        onClick={redirectUrl}
      />
      <img
        width={'56px'}
        height={'56px'}
        src={initData?.isEpg ? initData?.epgLogoForOtp : ozone_login_logo}
        alt='ozone logo'
        className='mt-[25%] rounded-xl'
      />

      <OTP
        otpExpireDate={initData?.otpExpireDate!}
        isLoading={manualLoading}
        btnLoading={loginPending}
        phoneNumber={initData?.clientMobileNumber!}
        submittedOtp={handleSubmit}
        redirectUrl={redirectUrl}
      />
    </Container>
  );
};
export default Login;
